import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
// import axios from 'axios';
// import host from '../../global.jsx';
import Loader from '../Loader.jsx';
import { fetchAuthSession, signOut } from '../../utils/authSession';
import DashboardContent from './DashboardContent';
import OrganisationContent from './OrganisationContent';
import RequestDemoContent from './RequestDemoContent.jsx';
import AccountInfo from './AccountInfo';
import Feedback from './Feedback';
import SupportInbox from './SupportInbox.jsx';
import CandidateFeedbackDetails from './CandidateFeedbackDetails.jsx';
import TicketsContent from './TicketsContent.jsx';
import ImpersonationContent from './ImpersonationContent.jsx';
import QuickActionsContent from './QuickActionsContent.jsx';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import Pagination from "./Pagination";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeaderSidebarAndTopbar from './components/extras/AdminHeaderSidebar.jsx';
import CustomDropdown from './components/extras/CustomDropdown.jsx';
import useClickOutside from './hooks/useClickOutside';
import { useAdminProfile } from './hooks/useAdminProfile';
import { useDashboardData } from './hooks/useDashboardData';
import { adminDashboardService } from './services/adminDashboardService';
import { useAdminAuth } from './hooks/useAdminAuth.js';
import { validateOrganizationForm } from './utils/formValidationHelpers.js';

// MUI Icon Imports
import ExitToApp from '@mui/icons-material/ExitToApp';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

// Lucide React Icons
import { UserPen, LogOut, ChevronDown, SlidersHorizontal, Zap } from "lucide-react";

const GenieHireAdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // const [isLoading, setIsLoading] = useState(true);
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isLoading, isAuthenticated } = useAdminAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  // const [userData, setUserData] = useState(null);
  // const [userId, setUserId] = useState(null);
  // const [profileImageUrl, setProfileImageUrl] = useState("");
  const { userData, userId, profileImageUrl, loading: profileLoading } = useAdminProfile();
  const { pendingProfiles, demoProfiles, loading: dataLoading, refetch } = useDashboardData();  

  // New useEffect to update showOrganisations based on route
  useEffect(() => {
    // Check if current path matches /geniehireadmin/organisation/:organisation
    const orgDetailRegex = /^\/geniehireadmin\/organisation\/[^/]+$/;
    if (orgDetailRegex.test(location.pathname)) {
      setShowOrganisations(false);
    } else if (location.pathname === "/geniehireadmin/organisation") {
      setShowOrganisations(true);
    }
  }, [location.pathname]);

  // useEffect to set state based on current URL on mount and location change
  useEffect(() => {
    const path = location.pathname;
    // Reset all states first
    setShowDashboard(false);
    setShowOrganisations(false);
    setShowRequestDemo(false);
    setShowDemo(false);
    setShowTickets(false);
    setShowAccountInfo(false);
    setShowFeedback(false);
    setShowImpersonation(false);
    setShowQuickActions(false);
    setShowSupportInbox(false);
    setShowCandidateFeedback(false);

    // Set the appropriate state based on path
    if (path === "/geniehireadmin/dashboard") {
      setShowDashboard(true);
    } else if (path === "/geniehireadmin/organisation") {
      setShowOrganisations(true);
    } else if (/^\/geniehireadmin\/organisation\/[^/]+$/.test(path)) {
      // For organisation detail pages, don't set showOrganisations so it uses Outlet
      setShowOrganisations(false);
    } else if (path === "/geniehireadmin/mic-health") {
      // Rendered via Outlet (child route in App.jsx), like organisation detail
    } else if (path === "/geniehireadmin/requestdemo") {
      setShowRequestDemo(true);
    } else if (path === "/geniehireadmin/tickets") {
      setShowTickets(true);
    } else if (path === "/geniehireadmin/feedback") {
      setShowFeedback(true);
    } else if (path === "/geniehireadmin/impersonate") {
      setShowImpersonation(true);
    } else if (path === "/geniehireadmin/quickactions") {
      setShowQuickActions(true);
    } else if (path === "/geniehireadmin/support-inbox") {
      setShowSupportInbox(true);
    } else if (path === "/geniehireadmin/candidate-feedback") {
      setShowCandidateFeedback(true);
    } else {
      // Default to dashboard if no match
      setShowDashboard(true);
    }
  }, [location.pathname]);

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const [showDemo, setShowDemo] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showOrganisations, setShowOrganisations] = useState(false);
  const [showRequestDemo, setShowRequestDemo] = useState(false);
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showImpersonation, setShowImpersonation] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showSupportInbox, setShowSupportInbox] = useState(false);
  const [showCandidateFeedback, setShowCandidateFeedback] = useState(false);

  // const [PendingProfiles, setPendingProfiles] = useState(null);
  // const [DemoProfiles, setDemoProfiles] = useState(null);

  const [viewTicket, setViewTicket] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Ticket modal states
  const demoModalRef = useRef(null);
  const formModalRef = useRef(null);
  const errorModalRef = useRef(null);
  const successModalRef = useRef(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Use scroll lock for modals
  useBodyScrollLock(
    formVisible ||
      viewTicket ||
      showSuspendModal ||
      // showTicketsModal ||
      showDemoModal ||
      showFeedbackModal
      // isTicketModalOpen
  );

  useClickOutside(
    [demoModalRef, formModalRef, errorModalRef, successModalRef],
    () => {
      if (showDemoModal) setShowDemoModal(false);
      if (formVisible) {
        setFormVisible(false);
        resetFormData();
      }
      if (isErrorModalOpen) setIsErrorModalOpen(false);
      if (isSuccessModalOpen) setIsSuccessModalOpen(false);
    }
  );

  const [formData, setFormData] = useState({
    organization_name: "",
    org_admin_name: "",
    org_admin_email: "",
    org_admin_contact: "",
    total_hr_allocated: "5",
    timezone: "Asia/Kolkata",
    account_type: "Trial",
    account_manager_name: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    organization_name: "",
    org_admin_name: "",
    org_admin_email: "",
    org_admin_contact: "",
    total_hr_allocated: "",
    timezone: "",
    account_type: "",
    account_manager_name: "",
  });

   const convaiadmin_accessToken = sessionStorage.getItem("accessToken");

  // Sidebar items configuration
  const sidebarItems = [
    {
      iconPath: "/homesidebar.png",
      hoverIconPath: "/homesidebarhover.png",
      label: "Dashboard",
      active: showDashboard,
      onClick: () => {
        navigate("/geniehireadmin/dashboard");
        setShowDashboard(true);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
      },
    },
    {
      iconPath: "/office-building-bnw.png",
      hoverIconPath: "/office-building.png",
      label: "Accounts",
      active: showOrganisations,
      onClick: () => {
        navigate("/geniehireadmin/organisation");
        setShowDashboard(false);
        setShowOrganisations(true);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
      },
    },
    {
      iconPath: "/Demo-bnw.png",
      hoverIconPath: "/Demo.png",
      label: "Request Demo",
      active: showRequestDemo,
      onClick: () => {
        navigate("/geniehireadmin/requestdemo");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(true);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
      },
    },
    {
      iconPath: "/reportssidebar.png",
      hoverIconPath: "/reportssidebarhover.png",
      label: "Feedback",
      active: showFeedback,
      onClick: () => {
        navigate("/geniehireadmin/feedback");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(true);
        setShowImpersonation(false);
      },
    },
    {
      iconPath: "/raiseticketsidebar.png",
      hoverIconPath: "/raiseticketsidebarhover.png",
      label: "Raised Tickets",
      active: showTickets,
      onClick: () => {
        navigate("/geniehireadmin/tickets");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(true);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
      },
    },
    {
      iconPath: "/teamsidebar.png",
      hoverIconPath: "/teamsidebarhover.png",
      label: "Impersonate",
      active: showImpersonation,
      onClick: () => {
        navigate("/geniehireadmin/impersonate");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(true);
        setShowQuickActions(false);
      },
    },
    {
      iconPath: "/raiseticketsidebar.png", // Using existing icon path logic
      hoverIconPath: "/raiseticketsidebarhover.png",
      label: "Quick Actions",
      active: showQuickActions,
      onClick: () => {
        navigate("/geniehireadmin/quickactions");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
        setShowQuickActions(true);
        setShowSupportInbox(false);
      },
    },
    {
      iconPath: "/reportssidebar.png",
      hoverIconPath: "/reportssidebarhover.png",
      label: "Candidate Feedback",
      active: showCandidateFeedback,
      onClick: () => {
        navigate("/geniehireadmin/candidate-feedback");
        setShowDashboard(false);
        setShowOrganisations(false);
        setShowRequestDemo(false);
        setShowDemo(false);
        setShowTickets(false);
        setShowAccountInfo(false);
        setShowFeedback(false);
        setShowImpersonation(false);
        setShowQuickActions(false);
        setShowSupportInbox(false);
        setShowCandidateFeedback(true);
      },
    },
  ];

  const toggleVisibility = (setter, current) => {
    setShowDashboard(false);
    setShowOrganisations(false);
    setShowRequestDemo(false);
    setShowDemo(false);
    setShowTickets(false);
    setShowAccountInfo(false);
    setShowFeedback(false);
    setShowImpersonation(false);
    setShowQuickActions(false);
    setShowSupportInbox(false);
    setShowCandidateFeedback(false);
    setter(!current);
  };

  const resetFormData = () => {
    setFormData({
      organization_name: "",
      org_admin_name: "",
      org_admin_email: "",
      org_admin_contact: "",
      total_hr_allocated: "5",
      timezone: "Asia/Kolkata",
      account_type: "Trial",
      account_manager_name: "",
    });
    setValidationErrors({
      organization_name: "",
      org_admin_name: "",
      org_admin_email: "",
      org_admin_contact: "",
      total_hr_allocated: "",
      timezone: "",
      account_type: "",
      account_manager_name: "",
    });
  };

  const handleAddCompany = () => {
    resetFormData();
    setFormVisible(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // Use the shared utility function!
    const errorMessage = validateOrganizationForm(name, value);

    setValidationErrors({
      ...validationErrors,
      [name]: errorMessage,
    });

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Custom handler for dropdown changes
  const handleDropdownChange = (name, value) => {
    let errorMessage = "";

    switch (name) {
      case "timezone":
        errorMessage = !value ? "Please select a timezone" : "";
        break;
      case "account_type":
        errorMessage = !value ? "Please select an account type" : "";
        break;
      case "account_manager_name":
        errorMessage = !value ? "Please select an account manager" : "";
        break;
      default:
        break;
    }

    setValidationErrors({
      ...validationErrors,
      [name]: errorMessage,
    });

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Detect browser autofill and update form state
  useEffect(() => {
    if (formVisible) {
      const checkAutofill = () => {
        const inputs = document.querySelectorAll("input, select");
        inputs.forEach((input) => {
          if (input.value && !formData[input.name]) {
            // Input has value but form state doesn't - likely autofilled
            handleFormChange({ target: input });
          }
        });
      };

      // Check for autofill after a short delay to allow browser to fill
      const timer = setTimeout(checkAutofill, 100);
      return () => clearTimeout(timer);
    }
  }, [formVisible, formData, handleFormChange]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      await adminDashboardService.createOrgAdmin(formDataToSend);
      setSuccessMessage("Account added successfully.");
      setIsSuccessModalOpen(true);
      setFormVisible(false);
      refetch(); // Refresh data after adding
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      setErrorMessage(
        detail.toLowerCase().includes("user already exists")
          ? "An account with this email address already exists."
          : detail || "Failed to add organization"
      );
      setIsErrorModalOpen(true);
    }
  };

  const acceptDemo = async (demoId) => {
    try {
      await adminDashboardService.updateDemoStatus(demoId, 'accepted');
      refetch(); // Refresh data
    } catch (err) {
      console.error("Error accepting demo", err);
    }
  };

  const rejectDemo = async (demoId) => {
    try {
      await adminDashboardService.updateDemoStatus(demoId, 'rejected');
      refetch(); // Refresh data
    } catch (err) {
      console.error("Error rejecting demo", err);
    }
  };

  if (isLoading) return <Loader />;
  if (!isAuthenticated) return null;

  return (
    <div
      className="min-h-screen bg-cover bg-center flex relative"
      style={{
        backgroundImage: "url('/BG.png')",
        backgroundAttachment: "fixed",
      }}
    >
      {/* CSS to prevent browser autofill highlighting and styling */}
      <style>
        {`
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active,
          select:-webkit-autofill,
          select:-webkit-autofill:hover,
          select:-webkit-autofill:focus,
          select:-webkit-autofill:active,
          textarea:-webkit-autofill,
          textarea:-webkit-autofill:hover,
          textarea:-webkit-autofill:focus,
          textarea:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
            -webkit-text-fill-color: #CCCCCC !important;
            background-color: transparent !important;
            background-image: none !important;
            color: #CCCCCC !important;
            border-color: rgba(255, 255, 255, 0.1) !important;
            transition: background-color 50000s ease-in-out 0s, color 50000s ease-in-out 0s;
          }
          
          /* For Firefox */
          input:-moz-autofill,
          input:-moz-autofill:hover,
          input:-moz-autofill:focus,
          input:-moz-autofill:active,
          select:-moz-autofill,
          select:-moz-autofill:hover,
          select:-moz-autofill:focus,
          select:-moz-autofill:active {
            background-color: transparent !important;
            color: #CCCCCC !important;
            border-color: rgba(255, 255, 255, 0.1) !important;
          }
          
          /* Standard property for modern browsers */
          input:autofill,
          input:autofill:hover,
          input:autofill:focus,
          input:autofill:active,
          select:autofill,
          select:autofill:hover,
          select:autofill:focus,
          select:autofill:active {
            background-color: transparent !important;
            color: #CCCCCC !important;
            border-color: rgba(255, 255, 255, 0.1) !important;
          }
          
          /* Ensure consistent styling for all form elements */
          input, select, textarea {
            background-color: transparent !important;
            color: #CCCCCC !important;
          }
          
          /* Remove any focus highlighting that might interfere */
          input:focus, select:focus, textarea:focus {
            outline: none !important;
            box-shadow: none !important;
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
        `}
      </style>
      <AdminHeaderSidebarAndTopbar
        showDashboard={showDashboard}
        showOrganisations={showOrganisations}
        showRequestDemo={showRequestDemo}
        showTickets={showTickets}
        showFeedback={showFeedback}
        showImpersonation={showImpersonation}
        showQuickActions={showQuickActions}
        showSupportInbox={showSupportInbox}
        showCandidateFeedback={showCandidateFeedback}
        setShowDashboard={setShowDashboard}
        setShowOrganisations={setShowOrganisations}
        setShowRequestDemo={setShowRequestDemo}
        setShowTickets={setShowTickets}
        setShowFeedback={setShowFeedback}
        setShowAccountInfo={setShowAccountInfo}
        setShowImpersonation={setShowImpersonation}
        setShowQuickActions={setShowQuickActions}
        setShowSupportInbox={setShowSupportInbox}
        setShowCandidateFeedback={setShowCandidateFeedback}
        DemoProfiles={demoProfiles}
        toggleShowDemo={() => setShowDemoModal(prev => !prev)}
        handleAddCompany={handleAddCompany}
        profileImageUrl={profileImageUrl}
        userRole={userData?.name || "Admin"}
      />

        {/* Content */}
        <div className="pt-20 px-8 pb-8 ml-20 w-full">
          {showDashboard && <DashboardContent />}
          {showOrganisations && <OrganisationContent />}
          {showRequestDemo && <RequestDemoContent />}

          {/* Tickets Content */}
          {showTickets && <TicketsContent />}

          {showAccountInfo && <AccountInfo />}
          {showFeedback && <Feedback />}
          {showImpersonation && <ImpersonationContent />}
          {showQuickActions && <QuickActionsContent />}
          {showSupportInbox && <SupportInbox />}
          {showCandidateFeedback && <CandidateFeedbackDetails />}
          {!showDashboard &&
            !showOrganisations &&
            !showRequestDemo &&
            !showDemo &&
            !showTickets &&
            !showAccountInfo &&
            !showFeedback &&
            !showImpersonation &&
            !showSupportInbox &&
            !showCandidateFeedback &&
            !viewTicket && <Outlet />}

          {/* Add Organization Modal */}
          {formVisible && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                style={{
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              ></div>
              <div
                ref={formModalRef}
                className="w-full max-w-xl p-6 rounded-lg shadow-lg relative max-h-[75vh] overflow-y-auto"
                style={{
                  background:
                    "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(50px)",
                  borderRadius: "10px",
                }}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-[#C1A0D0] text-left text-lg">
                    Add New Account
                  </h3>
                  <button
                    onClick={() => {
                      setFormVisible(false);
                      resetFormData();
                    }}
                  >
                    <CloseRoundedIcon className="h-5 w-5 text-white cursor-pointer hover:text-gray-300" />
                  </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-4 mt-10">
                  <div>
                    <span className="text-[#CCCCCC] text-sm">
                      Account Name*
                    </span>
                    <input
                      type="text"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none"
                      placeholder="Enter organization name"
                    />
                    {validationErrors.organization_name && (
                      <span className="text-red-400 text-xs mt-1">
                        {validationErrors.organization_name}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">Admin Name*</span>
                    <input
                      type="text"
                      name="org_admin_name"
                      value={formData.org_admin_name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none"
                      placeholder="Enter admin name"
                    />
                    {validationErrors.org_admin_name && (
                      <span className="text-red-400 text-xs mt-1">
                        {validationErrors.org_admin_name}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">Admin Email*</span>
                    <input
                      type="email"
                      name="org_admin_email"
                      value={formData.org_admin_email}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none"
                      placeholder="Enter admin email"
                    />
                    {validationErrors.org_admin_email && (
                      <span className="text-red-400 text-xs mt-1">
                        {validationErrors.org_admin_email}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">
                      Admin Contact*
                    </span>
                    <input
                      type="text"
                      name="org_admin_contact"
                      value={formData.org_admin_contact}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none"
                      placeholder="Enter admin contact"
                    />
                    {validationErrors.org_admin_contact && (
                      <span className="text-red-400 text-xs mt-1">
                        {validationErrors.org_admin_contact}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">
                      Number of HRs*
                    </span>
                    <input
                      type="number"
                      name="total_hr_allocated"
                      value={formData.total_hr_allocated}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-400 mt-2 focus:outline-none"
                      placeholder="Enter number of HRs"
                      min="1"
                      max="10"
                    />
                    {validationErrors.total_hr_allocated && (
                      <span className="text-red-400 text-xs mt-1">
                        {validationErrors.total_hr_allocated}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">Timezone*</span>
                    <CustomDropdown
                    options={[
                      { value: "Asia/Kolkata", label: "India (IST) - Mumbai, Delhi" },
                      { value: "America/New_York", label: "USA East (EST/EDT) - New York" },
                      { value: "America/Los_Angeles", label: "USA West (PST/PDT) - Los Angeles" },
                      { value: "America/Chicago", label: "USA Central (CST/CDT) - Chicago" },
                      { value: "Europe/London", label: "UK (GMT/BST) - London" },
                      { value: "Europe/Berlin", label: "Germany (CET/CEST) - Berlin" },
                      { value: "Europe/Paris", label: "France (CET/CEST) - Paris" },
                      { value: "Asia/Tokyo", label: "Japan (JST) - Tokyo" },
                      { value: "Asia/Shanghai", label: "China (CST) - Beijing, Shanghai" },
                      { value: "Asia/Singapore", label: "Singapore (SGT)" },
                      { value: "Asia/Dubai", label: "UAE (GST) - Dubai" },
                      { value: "Australia/Sydney", label: "Australia East (AEDT/AEST) - Sydney" },
                      { value: "America/Toronto", label: "Canada East (EST/EDT) - Toronto" },
                      { value: "America/Sao_Paulo", label: "Brazil (BRT) - São Paulo" },
                      { value: "Africa/Johannesburg", label: "South Africa (SAST) - Johannesburg" },
                      { value: "Asia/Bangkok", label: "Thailand (ICT) - Bangkok" },
                      { value: "Europe/Amsterdam", label: "Netherlands (CET/CEST) - Amsterdam" },
                      { value: "Asia/Seoul", label: "South Korea (KST) - Seoul" },
                      { value: "America/Mexico_City", label: "Mexico (CST/CDT) - Mexico City" },
                      { value: "Europe/Rome", label: "Italy (CET/CEST) - Rome" },
                    ]}
                    value={formData.timezone}
                    onChange={(value) => handleDropdownChange("timezone", value)}
                    placeholder="Select timezone"
                    className="mt-2"
                    error={!!validationErrors.timezone}
                  />
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">
                      Account Type*
                    </span>
                    <CustomDropdown
                      options={[
                        { value: "Trial", label: "Trial" },
                        { value: "Basic", label: "Basic" },
                        { value: "Intermediate", label: "Intermediate" },
                        { value: "Advanced", label: "Advanced" },
                      ]}
                      value={formData.account_type}
                      onChange={(value) => handleDropdownChange("account_type", value)}
                      placeholder="Select account type"
                      className="mt-2"
                      error={!!validationErrors.account_type}
                    />
                  </div>

                  <div>
                    <span className="text-[#CCCCCC] text-sm">
                      Account Manager Name*
                    </span>
                    <CustomDropdown
                      options={[
                        { value: "Gayatri", label: "Gayatri" },
                        { value: "Ritika", label: "Ritika" },
                        { value: "Anushka", label: "Anushka" },
                        { value: "Nsquare Admin", label: "Nsquare Admin" },
                      ]}
                      value={formData.account_manager_name}
                      onChange={(value) => handleDropdownChange("account_manager_name", value)}
                      placeholder="Select account manager"
                      className="mt-2"
                      error={!!validationErrors.account_manager_name}
                    />
                  </div>

                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      disabled={
                        !formData.organization_name.trim() ||
                        !formData.org_admin_name.trim() ||
                        !formData.org_admin_email.trim() ||
                        !formData.org_admin_contact.trim() ||
                        !formData.account_manager_name.trim() ||
                        Object.values(validationErrors).some(
                          (error) => error !== ""
                        )
                      }
                      className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Account
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Demo Requests Modal */}
          {showDemoModal && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                style={{
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              ></div>
              <div
                ref={demoModalRef}
                className="glass-effect rounded-lg p-6 w-full max-w-5xl mx-auto shadow-lg overflow-y-auto max-h-[80vh]"
                style={{
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(50px)",
                }}
              >
                <div className="flex justify-between items-center mb-4">
                  <p className="text-lg font-light text-white">Demo Requests</p>
                  <CloseRoundedIcon
                    className="text-white cursor-pointer hover:text-gray-300"
                    onClick={() => setShowDemoModal(false)}
                  />
                </div>

                <hr className="border-gray-600 mb-4" />

                {demoProfiles && demoProfiles.length > 0 ? (
                  <div className="space-y-4">
                    {demoProfiles.map((demo) => (
                      <div
                        key={demo._id}
                        className="max-w-4xl mx-auto" // This reduces width and centers
                      >
                        <div
                          className="rounded-xl p-4 w-full"
                          style={{
                            background:
                              "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                            backdropFilter: "blur(40px)",
                            WebkitBackdropFilter: "blur(50px)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "10px",
                          }}
                        >
                          <div className="flex justify-between items-center">
                            {/* Left Content */}
                            <div className="flex-1">
                              <h3 className="text-white font-semibold text-lg mb-2">
                                {demo.name}
                              </h3>

                              {demo.workEmail && demo.phoneNumber && (
                                <div className="flex items-center gap-3 text-gray-300 mb-2">
                                  <span className="text-gray-400">
                                    {demo.phoneNumber}
                                  </span>
                                  <span className="text-gray-500">|</span>
                                  <span className="text-gray-300">
                                    {demo.workEmail}
                                  </span>
                                </div>
                              )}

                              <p className="text-gray-400 text-sm">
                                {new Date(demo.created_at).toLocaleDateString()}
                              </p>
                            </div>

                            {/* Buttons */}
                            <div className="flex space-x-3">
                              <button
                                onClick={() => acceptDemo(demo._id)}
                                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg transition-colors font-medium whitespace-nowrap"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => rejectDemo(demo._id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg transition-colors font-medium whitespace-nowrap"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-300 text-center py-8">
                    No demo requests found.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error Modal */}
          {isErrorModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                style={{
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              ></div>
              <div
                ref={errorModalRef}
                className="glass-effect rounded-lg p-6 w-full max-w-md mx-auto shadow-lg"
                style={{
                  background:
                    "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(50px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                }}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-white">Error</h3>
                  <CloseRoundedIcon
                    className="text-red-400 cursor-pointer hover:text-red-300"
                    onClick={() => setIsErrorModalOpen(false)}
                  />
                </div>
                <div className="text-gray-300 mb-6">{errorMessage}</div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsErrorModalOpen(false)}
                    className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Modal */}
          {isSuccessModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                style={{
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              ></div>
              <div
                ref={successModalRef}
                className="glass-effect rounded-lg p-6 w-full max-w-md mx-auto shadow-lg"
                style={{
                  background:
                    "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(50px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                }}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-white">Success</h3>
                  <CloseRoundedIcon
                    className="text-red-400 cursor-pointer hover:text-red-300"
                    onClick={() => setIsSuccessModalOpen(false)}
                  />
                </div>
                <div className="text-gray-300 mb-6">{successMessage}</div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsSuccessModalOpen(false)}
                    className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      {/* </div> */}
      {/* Add this just before the closing </div> of the main container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="text-sm font-medium"
        style={{ zIndex: 999999999 }}
      />
    </div>
  );
};

export default GenieHireAdminDashboard;
